import React, { useCallback, useEffect, useState } from 'react';

import { Spin } from 'antd-v5';
import type { FormProps } from 'antd/lib/form';
import { concat, find, get, map } from 'lodash';
import ReactDOM from 'react-dom';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import {
  usePostEmailAttachmentMutation,
  usePostEmailMutation,
} from '@modules/messaging/api';
import Member from '@modules/messaging/components/Member';
import { useMbcMessageRecipients } from '@modules/messaging/hooks';
import { Button, EditorTextArea, Form, Input, T } from '@shared/components';
import { organizationGuidSel } from '@shared/duck/selectors/general.selectors';
import { intl } from '@shared/localization/translate';
import { toastService } from '@toasts';
import {
  unitInfoRequest,
  unitInfoRequestIfNotLoaded,
} from '@unit/duck/actions';
import { unitInfoSel } from '@unit/duck/selectors';

import Attachment from '../AttachmentAdd';
import AttachmentFiles from '../AttachmentFiles';
import ManageMBCYouthsMembersModal from '../ManageMBCYouthsMembersModal';
import SubjectCharactersTooltip from '../SubjectCharactersTooltip';
import styles from './MbcMessage.less';

const FormItem = Form.Item;

const MbcMessage = ({ form }: { form: NonNullable<FormProps['form']> }) => {
  const isBCCEnabled = featureFlags.getFlag('SBL_6753_ENABLE_BCC_MESSAGING');
  //TODO: remove this flag, it should exist just to test not to go to PROD
  const isLaxSubjectPattern = featureFlags.getFlag(
    'SBL_6856_LAX_MESSAGING_CHARACTERS',
  );
  const isAttachmentEnabled = featureFlags.getFlag(
    'SBL_4447_ENABLE_ATTACHMENT_MESSAGING',
  );
  const dispatch = useDispatch();
  const unitInfo = useSelector(unitInfoSel);
  const { getFieldDecorator } = form;
  const [manageMembersModalVisible, setManageMembersModalVisible] =
    useState<boolean>(false);
  const [isBCCRecipients, setIsBCCRecipients] = useState<boolean>(false);
  const [recipients, setRecipients] = useState<number[]>([]);
  const [bccRecipients, setBCCRecipients] = useState<number[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const currentOrganizationGuid = useSelector(organizationGuidSel);
  const [postEmail, { isLoading: isEmailLoading }] = usePostEmailMutation();
  const [postEmailAttachment, { isLoading: isAttachmentLoading }] =
    usePostEmailAttachmentMutation();

  const { youths, isLoadingMbcProfile } = useMbcMessageRecipients();

  const isLoading = isEmailLoading || isAttachmentLoading;
  // Regexp used by ESB validation with all allowed characters for subject per SBL-5157
  const subjectPattern = new RegExp(
    `^[\u00E9\u00C9\u00E8\u00C8\u00E1\u00C1\u00E2\u00C2\u00ED\u00CD\u00EE\u00CE\u00F3\u00D3\u00F4\u00D4\u00FA\u00DA\u00FC\u00DC\u00EF\u00CF\u00E7\u00C7\u00F1\u00D8\u0021\u00D1#\\s,:_;@%+!*'’&/A-Za-z0-9\\.\\-\\–\\(\\)${
      isLaxSubjectPattern && '\\[\\]'
    }]*$`,
  );

  useEffect(() => {
    dispatch(unitInfoRequest());
  }, [dispatch]);

  useEffect(() => {
    if (unitInfo.organizationGuid !== currentOrganizationGuid) {
      dispatch(unitInfoRequestIfNotLoaded());
    }
  }, [dispatch, unitInfo, currentOrganizationGuid]);

  useEffect(() => {
    setRecipients([]);
    setBCCRecipients([]);
    form.resetFields(['subject', 'body']);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganizationGuid]);

  const selectedRecipients = (items: number[]) => {
    const list = concat(
      youths.map(youth => youth.relationships[0]),
      youths,
    );
    return map(items, (recipient: number) => {
      const matchedRecipient = find(list, item => item?.memberId === recipient);
      if (matchedRecipient) {
        return (
          <Member key={`member-${recipient}`} recipient={matchedRecipient} />
        );
      }
      return null;
    });
  };

  const clear = useCallback(() => {
    setRecipients([]);
    setBCCRecipients([]);
    setFiles([]);
    form.resetFields(['subject', 'body']);
  }, [form]);

  const sendMessage = async () => {
    try {
      form.validateFields(async (err, formValues) => {
        if (!err) {
          const footer = document.createElement('div');
          ReactDOM.render(
            <div>
              <div dangerouslySetInnerHTML={{ __html: formValues.body }} />
            </div>,
            footer,
          );
          const payload = {
            to: {
              memberId: [],
            },
            cc: {
              memberId: recipients,
            },
            bcc: {
              memberId: bccRecipients,
            },
            subject: formValues.subject,
            body: footer.outerHTML,
          };

          if (files.length > 0) {
            const formData = new FormData();
            formData.append('data', JSON.stringify(payload));
            files.forEach(file => {
              formData.append('files', file, file.name);
            });

            await postEmailAttachment({
              payload: formData,
              organizationGuid: currentOrganizationGuid,
            }).unwrap();

            setFiles([]);
          } else {
            await postEmail({
              payload,
              organizationGuid: currentOrganizationGuid,
            }).unwrap();
          }

          toastService.success(
            intl.formatMessage({
              id: 'messaging.Message.emailSent',
            }),
          );
          setRecipients([]);
          setBCCRecipients([]);
        }
      });
    } catch (error) {
      const errorMessage = get(error, ['data', 'errorDesc']);
      if (errorMessage) {
        toastService.error(errorMessage);
      } else {
        toastService.error(
          intl.formatMessage({
            id: 'messaging.Message.failedToSendEmail',
          }),
        );
      }
    }
  };

  return (
    <Spin spinning={isLoading}>
      <div className={styles.header}>
        <T size="1">
          <FormattedMessage id="messaging.Message.newEmail" />
        </T>
      </div>

      <ManageMBCYouthsMembersModal
        visible={manageMembersModalVisible}
        onRecipientsUpdate={memberIds =>
          isBCCRecipients
            ? setBCCRecipients(memberIds)
            : setRecipients(memberIds)
        }
        onCancel={() => setManageMembersModalVisible(false)}
        recipients={isBCCRecipients ? bccRecipients : recipients}
      />

      <div className={styles.recipientsGrid}>
        <div>
          <div className={styles.selectedRecipients}>
            {recipients.length ? (
              selectedRecipients(recipients)
            ) : (
              <FormattedMessage id="messaging.Message.field.recipients" />
            )}
          </div>
          <div className={styles.note}>
            <FormattedMessage id="messaging.Message.recipients.note" />
          </div>
        </div>
        <Button
          shape="round"
          disabled={isLoadingMbcProfile}
          size="large"
          onClick={() => {
            setIsBCCRecipients(false);
            setManageMembersModalVisible(true);
          }}
        >
          <FormattedMessage
            id="messaging.Message.recipients"
            values={{ numberOfRecipients: recipients.length }}
          />
        </Button>
        {isBCCEnabled && (
          <div>
            <div className={styles.selectedRecipients}>
              {bccRecipients.length ? (
                selectedRecipients(bccRecipients)
              ) : (
                <FormattedMessage id="messaging.Message.field.bccRecipients" />
              )}
            </div>
            <div className={styles.note}>
              <FormattedMessage id="messaging.Message.bccRecipients.note" />
            </div>
          </div>
        )}
        {isBCCEnabled && (
          <Button
            shape="round"
            size="large"
            disabled={isLoadingMbcProfile}
            onClick={() => {
              setIsBCCRecipients(true);
              setManageMembersModalVisible(true);
            }}
          >
            <FormattedMessage
              id="messaging.Message.bccRecipients"
              values={{ numberOfRecipients: bccRecipients.length }}
            />
          </Button>
        )}
      </div>
      <Form layout="vertical" className={styles.form}>
        <FormItem>
          <div className={styles.subjectAttachments}>
            {getFieldDecorator('subject', {
              initialValue: '',
              rules: [
                {
                  required: true,
                  message: intl.formatMessage({
                    id: 'shared.form.error.isRequired',
                  }),
                },
                {
                  pattern: subjectPattern,
                  message: intl.formatMessage({
                    id: 'messaging.Message.form.validation.subject.invalidCharacters',
                  }),
                },
              ],
            })(
              <Input
                size="large"
                placeholder={intl.formatMessage({
                  id: 'messaging.Message.form.subject',
                })}
                rounded
              />,
            )}

            <SubjectCharactersTooltip />

            {isAttachmentEnabled && (
              <Attachment files={files} setFiles={setFiles} />
            )}
          </div>
          {isAttachmentEnabled && (
            <AttachmentFiles files={files} setFiles={setFiles} />
          )}
        </FormItem>
        <div className={styles.editor}>
          <FormItem>
            {getFieldDecorator('body', {
              initialValue: '',
              rules: [
                {
                  required: true,
                  message: intl.formatMessage({
                    id: 'shared.form.error.isRequired',
                  }),
                },
              ],
            })(
              <EditorTextArea
                placeholder={intl.formatMessage({
                  id: 'messaging.Message.form.message',
                })}
                spellCheck={true}
                noBorder={true}
                toolbar={{
                  options: [
                    'block',
                    'history',
                    'indent-block',
                    'inline',
                    'link',
                    'list',
                  ],
                }}
              />,
            )}
          </FormItem>
        </div>
        <div className={styles.bottomActions}>
          <Button
            size="large"
            type="primary"
            shape="round"
            disabled={
              (recipients.length === 0 && bccRecipients.length === 0) ||
              isLoading
            }
            onClick={sendMessage}
          >
            <FormattedMessage id="messaging.Message.sendEmail" />
          </Button>
          <Button size="large" shape="round" onClick={clear}>
            <FormattedMessage id="messaging.Message.clear" />
          </Button>
        </div>
      </Form>
    </Spin>
  );
};

export default Form.create({
  onValuesChange: () => {},
})(MbcMessage);
